import { defineStore } from "pinia";

export const useWcagStore = defineStore("wcagStore", {
  state: () => {
    return {
      score: <any>null,
      url: <string>"",
      email: <string>"",
      results: {
        passed: <object>{},
        skipped: <object>{},
        warnings: <object>{},
      },
      totals: {
        errors: <number>0,
        passed: <number>0,
        skipped: <number>0,
        testable: <number>0,
        tested: <number>0,
        warnings: <number>0,
      },
      meta: {
        type: <string>"",
        hasAccessibilityStatement: <Boolean>false,
        isRecentlyAudited: <Boolean>false,
        hasPlannedAudit: <Boolean>false,
      },
      audited: <Boolean>false,
    };
  },
  getters: {
    arrayPassed: (state: any) =>
      Object.entries(state.results?.passed || {}).map(([key, value]) => ({
        key,
        ...(typeof value === "object" && value !== null ? value : { value }),
      })),
    arrayFailed: (state: any) =>
      Object.entries(state.results?.errors || {}).map(([key, value]) => ({
        key,
        ...(typeof value === "object" && value !== null ? value : { value }),
      })),
    arraySkipped: (state: any) =>
      Object.entries(state.results?.skipped || {}).map(([key, value]) => ({
        key,
        ...(typeof value === "object" && value !== null ? value : { value }),
      })),
    arrayWarnings: (state: any) =>
      Object.entries(state.results?.warnings || {}).map(([key, value]) => ({
        key,
        ...(typeof value === "object" && value !== null ? value : { value }),
      })),
    advice: (state: any) => {
      let _advice = null;

      if (state.meta?.type === "private") {
        switch (state.score) {
          case "A":
            _advice =
              "Je lijkt het op orde te hebben! Op basis van jouw antwoorden en wat we kunnen zien, lijkt jouw webpagina te horen bij een website die voldoet aan de nieuwe wetgeving.";
            break;
          case "B":
            _advice =
              "Je bent prima op weg! Er zijn nog verbeterpunten, maar het lijkt erop dat je al werk hebt gemaakt van digitale toegankelijkheid. Van wat we kunnen zien, lijkt het erop dat jouw website voldoet aan de nieuwe wetgeving.";
            break;
          case "C":
            _advice =
              "Je bent op weg, maar je voldoet nog niet aan de nieuwe wetgeving. Er is onderzoek gedaan, maar dit moet nog worden doorvertaald naar een toegankelijkheidsverklaring. Je scoort een status 'C' en hebt minimaal status 'B' nodig om te voldoen aan de wetgeving omtrent digitale toegankelijkheid.";
            break;
          case "D":
            _advice =
              "Momenteel voldoe je nog niet aan de nieuwe wetgeving. Het wordt tijd, aangezien je eigenlijk al zou moeten voldoen. Je scoort een status 'D' en hebt minimaal status 'B' nodig om te voldoen aan de wetgeving omtrent digitale toegankelijkheid.";
            break;
          case "E":
            _advice =
              "Momenteel voldoe je nog niet aan de nieuwe wetgeving. Het wordt tijd, aangezien je eigenlijk al zou moeten voldoen. Je scoort een status 'E' en hebt minimaal status 'B' nodig om te voldoen aan de wetgeving omtrent digitale toegankelijkheid.";
            break;
        }
      } else if (state.meta?.type === "government") {
        switch (state.score) {
          case "A":
            _advice =
              "Je lijkt het op orde te hebben! Op basis van jouw antwoorden en wat we kunnen zien, lijkt jouw webpagina te horen bij een website die voldoet aan de nieuwe wetgeving.";
            break;
          case "B":
            _advice =
              "Je bent op weg, maar er zijn nog verbeterpunten. Het lijkt erop dat je al aandacht hebt voor digitale toegankelijkheid. Maar let op: sinds 28 juni 2025 dien je volledig te voldoen aan WCAG 2.1 AA criteria en moet je toewerken naar 2.2 AA criteria.";
            break;
          case "C":
            _advice =
              "Je bent op weg, maar je voldoet niet aan wetgeving. Het lijkt erop dat je al werkt aan digitale toegankelijkheid, maar let op: sinds 28 juni 2025 dien je volledig te voldoen aan WCAG 2.1 AA criteria en moet je toewerken naar 2.2 AA criteria.";
            break;
          case "D":
            _advice =
              "Momenteel voldoe je niet aan de nieuwe wetgeving. Het wordt tijd, aangezien je eigenlijk al zou moeten voldoen. Je scoort een status 'D' en dat is echt te laag. Overheidswebsites dienen sinds 28 juni 2025 te voldoen aan WCAG 2.1 AA criteria en toe te werken naar 2.2 AA criteria. Het is tijd voor actie.";
            break;
          case "E":
            _advice =
              "Momenteel voldoe je niet aan de nieuwe wetgeving. Het wordt tijd, aangezien je eigenlijk al zou moeten voldoen. Je scoort een status 'E' en dat is echt te laag. Overheidswebsites dienen sinds 28 juni 2025 te voldoen aan WCAG 2.1 AA criteria en toe te werken naar 2.2 AA criteria. Het is tijd voor actie.";
            break;
        }
      }

      return _advice;
    },
    status: (state: any) => {
      let _status = null;

      if (state.meta?.type === "private") {
        switch (state.score) {
          case "A":
            _status = "Jouw website voldoet aan de nieuwe wetgeving.";
            break;
          case "B":
            _status = "Jouw website voldoet aan de nieuwe wetgeving.";
            break;
          case "C":
            _status = "Jouw website voldoet nog niet aan de nieuwe wetgeving.";
            break;
          case "D":
            _status = "Jouw website voldoet nog niet aan de nieuwe wetgeving.";
            break;
          case "E":
            _status = "Jouw website voldoet nog niet aan de nieuwe wetgeving.";
            break;
        }
      } else if (state.meta?.type === "government") {
        switch (state.score) {
          case "A":
            _status = "Jouw website lijkt te voldoen aan de nieuwe wetgeving.";
            break;
          case "B":
            _status = "Jouw website voldoet nog niet aan de nieuwe wetgeving.";
            break;
          case "C":
            _status = "Jouw website voldoet nog niet aan de nieuwe wetgeving.";
            break;
          case "D":
            _status = "Jouw website voldoet nog niet aan de nieuwe wetgeving.";
            break;
          case "E":
            _status = "Jouw website voldoet nog niet aan de nieuwe wetgeving.";
            break;
        }
      }

      return _status;
    },
  },
  actions: {
    setScore(meta: any) {
      if (meta.type === "private") {
        // Logics for private organisation

        if (meta.hasAccessibilityStatement) {
          // Has accessibility statement
          if (this.totals.errors > 0 || this.totals.warnings > 0) {
            this.score = "B";
          } else {
            this.score = "A";
          }
        } else {
          if (meta.isRecentlyAudited) {
            // Recently audited
            this.score = "C";
          } else if (meta.hasPlannedAudit) {
            // Has planned audit in upcoming 6 months
            this.score = "D";
          } else {
            // Has no planned audit in upcoming 6 months
            this.score = "E";
          }
        }
      } else if (meta.type === "government") {
        // logics for governmental organisations

        if (meta.hasAccessibilityStatement) {
          // Has accessibility statement
          if (meta.isRecentlyAudited) {
            // Is recently audited
            if (this.totals.errors > 0 || this.totals.warnings > 0) {
              this.score = "B";
            } else {
              this.score = "A";
            }
          } else {
            if (meta.hasPlannedAudit) {
              // Has planned audit in upcoming 6 months
              this.score = "C";
            } else {
              // Has no planned audit in upcoming 6 months
              this.score = "D";
            }
          }
        } else {
          this.score = "E";
        }
      }
    },
    setUrl(value: string) {
      this.url = value;
    },
    setEmail(value: string) {
      this.email = value;
    },
    setResults(value: any) {
      this.results = value;
    },
    setTotals(value: any) {
      this.totals = value;
    },
    setType(value: any) {
      this.meta.type = value;
    },
    setAudited() {
      this.audited = true;
    },
    setHasAccessibilityStatement(value: any) {
      this.meta.hasAccessibilityStatement = value;
    },
    setIsRecentlyAudited(value: any) {
      this.meta.isRecentlyAudited = value;
    },
    setHasPlannedAudit(value: any) {
      this.meta.hasPlannedAudit = value;
    },
  },
});
